<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { FileArchive } from '@lucide/vue'
import {
  Button, Description, InlineAlert, Label, Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions,
  Radio, RadioField, RadioGroup, StateCard,
} from 'catalyst-vue'
import type { Comic } from '@tanko/shared'
import { useLibraryStore } from '../../stores/library'
import { errorMessage, type Candidate } from '../../lib/fs-access'
import { relinkEligibility } from '../../lib/comic-relink'
import { useControlSize } from '../../lib/library-layout'
import LibraryOverlay from './library-overlay.vue'

/**
 * 找不到檔案的作品要接到哪個候選上。
 *
 * 候選來自既有的掃描（與加入作品同一份），一次只接一本。可接上與否由 relinkEligibility
 * 決定，這裡只呈現結果：頁數或形態不同的候選列出但停用並說明——不自動配對，選哪一個是
 * 使用者的決定（產品原則四）。
 */
const props = defineProps<{ comic?: Comic }>()
const emit = defineEmits<{ close: []; done: [comic: Comic] }>()
const store = useLibraryStore()
const controlSize = useControlSize()
const sourceId = ref('')
const items = ref<Candidate[]>([])
const truncated = ref(false)
const chosen = ref('')
const loading = ref(false)
const saving = ref(false)
const error = ref('')
let generation = 0

/** 只列已授權的根來源：候選要掃描，未授權的讀不到。 */
const roots = computed(() => store.roots.filter(root => store.isAuthorized(root.id)))
const eligibility = computed(() => new Map(items.value.map(item => [item.relPath, props.comic ? relinkEligibility(props.comic, item) : { ok: false as const, reason: '' }])))
const selectable = computed(() => items.value.filter(item => eligibility.value.get(item.relPath)?.ok))

function statusOf(item: Candidate): string {
  const verdict = eligibility.value.get(item.relPath)
  return verdict && !verdict.ok ? verdict.reason : `${item.pageCount} 頁`
}

async function loadCandidates() {
  const current = ++generation
  chosen.value = ''
  items.value = []
  truncated.value = false
  error.value = ''
  if (!sourceId.value) return
  loading.value = true
  try {
    const result = await store.candidates(sourceId.value)
    if (current === generation) { items.value = result.candidates; truncated.value = result.truncated }
  } catch (cause) { if (current === generation) error.value = errorMessage(cause) }
  finally { if (current === generation) loading.value = false }
}

// 預設列這本作品自己的來源（若它是已授權的根），否則第一個已授權的根。
watch(() => props.comic?.id, async () => {
  if (!props.comic) return
  const own = roots.value.find(root => root.id === props.comic?.libraryId)
  sourceId.value = (own ?? roots.value[0])?.id ?? ''
  await loadCandidates()
}, { immediate: true })
watch(sourceId, () => { if (props.comic) void loadCandidates() })

async function apply() {
  const comic = props.comic
  if (!comic || !chosen.value || saving.value) return
  saving.value = true
  error.value = ''
  try {
    const relinked = await store.relinkComic(comic.id, { libraryId: sourceId.value, relPath: chosen.value })
    emit('done', relinked)
    emit('close')
  } catch (cause) { error.value = errorMessage(cause) }
  finally { saving.value = false }
}
</script>

<template>
  <LibraryOverlay
    :open="!!comic"
    size="2xl"
    title="更新作品位置"
    :description="`「${comic?.title ?? ''}」（${comic?.pageCount ?? 0} 頁）找不到檔案。選它現在所在的資料夾；分類、進度、書籤與雲端綁定都會跟著過去，硬碟上的檔案不會被改動。`"
    :pending="saving"
    @close="emit('close')"
  >
    <Listbox v-if="roots.length" v-model="sourceId" :disabled="saving">
      <ListboxButton class="add-source-select" :size="controlSize" aria-label="要從哪一個根資料夾列出候選" />
      <ListboxOptions>
        <ListboxOption v-for="root in roots" :key="root.id" :value="root.id">
          <ListboxLabel>{{ root.name }}</ListboxLabel>
        </ListboxOption>
      </ListboxOptions>
    </Listbox>

    <InlineAlert v-if="error" tone="error" class="mt-4">{{ error }}</InlineAlert>
    <StateCard v-if="loading" title="正在查看資料夾" message="往下尋找含有圖片的資料夾與壓縮檔…" :actionable="false" class="mt-4" />
    <template v-else>
      <StateCard v-if="!roots.length" title="沒有已開啟的根資料夾" message="回到書庫先開啟一個根資料夾的權限，才能列出它底下的候選。" :actionable="false" class="mt-4" />
      <StateCard v-else-if="items.length && !selectable.length && !error" title="這個來源底下沒有可接上的候選" message="可接上的候選要尚未加入書庫、形態相同、頁數相同。每一列的說明寫著它為什麼不行。" :actionable="false" class="mt-4" />
      <InlineAlert v-if="truncated" tone="warning" class="mt-4">有些分支太深沒有展開（最多往下四層）。若作品在更深的層級，請改選比較接近的資料夾當根目錄。</InlineAlert>
      <RadioGroup v-if="items.length" v-model="chosen" class="mt-5">
        <RadioField v-for="item in items" :key="item.relPath" :disabled="saving || !eligibility.get(item.relPath)?.ok" class="candidate-row">
          <Radio :value="item.relPath" color="primary" :aria-label="item.relPath" />
          <Label class="break-words candidate-label">
            <FileArchive v-if="item.kind === 'archive'" :size="14" aria-hidden="true" />{{ item.relPath }}
          </Label>
          <Description>{{ statusOf(item) }}</Description>
        </RadioField>
      </RadioGroup>
    </template>
    <template #actions>
      <Button outline :size="controlSize" :disabled="saving" @click="emit('close')">取消</Button>
      <Button color="primary" :size="controlSize" :disabled="loading || saving || !chosen" @click="apply">{{ saving ? '正在更新…' : '更新位置' }}</Button>
    </template>
  </LibraryOverlay>
</template>
